import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';
import { Icon } from '../ui/Icon';
import { Tabs, TabsList, TabsTrigger } from '../ui/Tabs2';
import { Button } from '../ui/Button2';
import { LoadingState } from '../ui/LoadingState';
import styles from './NotificationBell.module.css';

const CATEGORIES = [
  { id: 'ALL', label: 'All' },
  { id: 'THREAT', label: 'Threats' },
  { id: 'VULNERABILITY', label: 'Vulns' },
  { id: 'WORKFLOW', label: 'Workflow' },
  { id: 'SYSTEM', label: 'System' }
];

/**
 * Notification body text. Clamps to 2 lines by default; when the text is actually
 * long enough to be truncated, renders a "Show more"/"Show less" toggle so the user
 * can read the full message in place. The toggle only appears when the text overflows
 * the clamp, so short notifications stay clean.
 */
const NotificationMessage = ({ message }) => {
  const messageRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = messageRef.current;
    if (!el) return;
    if (expanded) return; // only measure in the clamped state
    // Clamped element overflows when its full height exceeds the visible box.
    setIsTruncated(el.scrollHeight > el.clientHeight + 1);
  }, [message, expanded]);

  const toggle = (e) => {
    e.stopPropagation(); // don't trigger the row's navigate-on-click
    setExpanded((v) => !v);
  };

  return (
    <>
      <p
        ref={messageRef}
        className={`${styles.message} ${expanded ? styles.messageExpanded : ''}`}
      >
        {message}
      </p>
      {(isTruncated || expanded) && (
        <button type="button" className={styles.showMoreButton} onClick={toggle}>
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </>
  );
};

const NotificationBell = () => {
  const navigate = useNavigate();
  
  // Add error boundary for context
  let contextValue;
  try {
    contextValue = useNotifications();
  } catch (error) {
    console.error('NotificationBell: Context not available', error);
    return null; // Don't render if context not available
  }
  
  const {
    notifications = [],
    unreadCounts = { total: 0, THREAT: 0, VULNERABILITY: 0, WORKFLOW: 0, SYSTEM: 0 },
    isLoading = false,
    selectedCategory = 'ALL',
    setSelectedCategory = () => {},
    fetchNotifications = () => {},
    refreshNotifications = () => {},
    markAsRead = () => {},
    markAllAsRead = () => {},
    setIsDropdownOpen = () => {}
  } = contextValue;

  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const toggleDropdown = () => {
    if (!isOpen && buttonRef.current) {
      // Calculate position for fixed dropdown
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
    setIsOpen(!isOpen);
  };

  // Update context when dropdown opens/closes
  useEffect(() => {
    setIsDropdownOpen(isOpen);
    if (isOpen) {
      // Fetch notifications when dropdown opens
      fetchNotifications(selectedCategory === 'ALL' ? null : selectedCategory);
    }
  }, [isOpen, setIsDropdownOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideContainer = dropdownRef.current && !dropdownRef.current.contains(event.target);
      const isOutsideDropdown = !event.target.closest('.notificationDropdownPanel');
      
      if (isOutsideContainer && isOutsideDropdown) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    fetchNotifications(categoryId === 'ALL' ? null : categoryId);
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (!notification.is_read) {
      await markAsRead(notification.notification_id);
    }

    // Navigate to action URL if provided
    if (notification.action_url) {
      setIsOpen(false);
      navigate(notification.action_url);
    }
  };

  const handleMarkAsRead = async (e, notificationId) => {
    e.stopPropagation();
    await markAsRead(notificationId);
  };

  const handleMarkAllAsRead = async () => {
    const category = selectedCategory === 'ALL' ? null : selectedCategory;
    await markAllAsRead(category);
  };

  const getRelativeTime = (timestamp) => {
    const now = new Date();
    const then = new Date(timestamp);
    const seconds = Math.floor((now - then) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return then.toLocaleDateString();
  };

  const filteredNotifications = notifications || [];
  const hasUnread = selectedCategory === 'ALL' 
    ? unreadCounts?.total > 0
    : unreadCounts?.[selectedCategory] > 0;

  return (
    <>
      <div className={styles.notificationContainer} ref={dropdownRef}>
        <button
          ref={buttonRef}
          className={styles.bellButton}
          onClick={toggleDropdown}
          aria-label="Notifications"
        >
          <Icon name="notification" size={20} />
          {unreadCounts?.total > 0 && (
            <span className={styles.badge}>
              {unreadCounts.total > 99 ? '99+' : unreadCounts.total}
            </span>
          )}
        </button>
      </div>

      {isOpen && createPortal(
        <div
          className={`${styles.dropdownPanel} notificationDropdownPanel`}
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            right: `${dropdownPosition.right}px`,
            zIndex: 10000
          }}
        >
          {/* Header */}
          <div className={styles.header}>
            <h3 className={styles.headerTitle}>Notifications</h3>
            <button 
              className={styles.refreshButton}
              onClick={refreshNotifications}
              disabled={isLoading}
              aria-label="Refresh notifications"
              title="Refresh notifications"
            >
              <Icon name="refresh" size={16} />
            </button>
          </div>

          {/* Category Tabs — 2.0 segmented (Tabs2 default variant) */}
          <div className={styles.tabs}>
            <Tabs
              value={selectedCategory}
              onValueChange={handleCategoryChange}
              className={styles.notifTabs}
            >
              <TabsList variant="default" className={styles.notifTabsList}>
                {CATEGORIES.map(category => (
                  <TabsTrigger
                    key={category.id}
                    value={category.id}
                    className={styles.notifTab}
                  >
                    {category.label}
                    {category.id !== 'ALL' && unreadCounts?.[category.id] > 0 && (
                      <span className={styles.tabCount}>{unreadCounts[category.id]}</span>
                    )}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          {/* Notifications List */}
          <div className={styles.notificationsList}>
            {isLoading ? (
              <LoadingState title="Loading notifications…" />
            ) : filteredNotifications.length === 0 ? (
              <div className={styles.emptyState}>
                <Icon name="notification" className={styles.emptyIcon} />
                <p className={styles.emptyText}>
                  No {selectedCategory !== 'ALL' ? selectedCategory.toLowerCase() : ''} notifications
                </p>
              </div>
            ) : (
              filteredNotifications.map(notification => (
                <div
                  key={notification.notification_id}
                  className={`${styles.notificationItem} ${!notification.is_read ? styles.unread : ''}`}
                >
                  {!notification.is_read && <div className={styles.unreadDot} />}

                  <div className={styles.content}>
                    <h4 className={styles.title}>{notification.title}</h4>
                    <NotificationMessage message={notification.message} />
                  </div>

                  <span className={styles.timestamp}>
                    {getRelativeTime(notification.created_at)}
                  </span>

                  {!notification.is_read && (
                    <Button
                      variant="outline"
                      size="xs"
                      className={styles.markReadButton}
                      onClick={(e) => handleMarkAsRead(e, notification.notification_id)}
                      aria-label="Mark as read"
                      startIcon={<Icon name="check" size={12} />}
                    >
                      Read
                    </Button>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {filteredNotifications.length > 0 && (
            <div className={styles.footer}>
              <Button
                variant="default"
                size="sm"
                onClick={handleMarkAllAsRead}
                disabled={!hasUnread}
              >
                Mark all as read
              </Button>
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

export default NotificationBell;
